import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { AudioRecorder } from './audio.recorder';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  tablerMicrophone,
  tablerPlayerPauseFilled,
  tablerPlayerPlayFilled,
  tablerPlayerStopFilled,
} from '@ng-icons/tabler-icons';

@Component({
  selector: 'oc-audio-recorder',
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [CommonModule, NgIconComponent],
  providers: [
    provideIcons({
      tablerPlayerPlayFilled,
      tablerPlayerStopFilled,
      tablerPlayerPauseFilled,
      tablerMicrophone,
    }),
  ],
  template: `
    <div *ngIf="!visualise" class="oc-audio-recorder">
      <div class="oc-audio-recorder-level" [style.transform]="'scale(' + (level + 1) + ')'"></div>
      <button *ngIf="!recorded" class="btn btn-danger btn-circle" type="button" (click)="record()">
        <ng-icon *ngIf="!recording" name="tablerMicrophone" size="24" color="fff"></ng-icon
        ><ng-icon *ngIf="recording" name="tablerPlayerStopFilled" size="24" color="fff"></ng-icon></button
      ><button *ngIf="recorded" class="btn btn-primary btn-circle" type="button" (click)="play()">
        <ng-icon *ngIf="playing" name="tablerPlayerPauseFilled" size="24" color="fff"></ng-icon>
        <ng-icon *ngIf="!playing" name="tablerPlayerPlayFilled" size="24" color="fff"></ng-icon>
      </button>
    </div>
    <div *ngIf="visualise" class="oc-audio-recorder-visual">
      <div
        class="oc-audio-recorder-visual-1"
        [style.height]="level * 50 + 4 + 'px'"
        [style.marginTop]="-(level * 25) - 2 + 'px'"
      ></div>
      <div
        class="oc-audio-recorder-visual-2"
        [style.height]="level * 100 + 4 + 'px'"
        [style.marginTop]="-(level * 50) - 2 + 'px'"
      ></div>
      <div
        class="oc-audio-recorder-visual-3"
        [style.height]="level * 50 + 4 + 'px'"
        [style.marginTop]="-(level * 25) - 2 + 'px'"
      ></div>
    </div>
  `,
  styles: `
    .oc-audio-recorder-visual {
      width: 100px;
      height: 100px;
      position: relative;
      margin: 20px auto;

      .oc-audio-recorder-visual-1 {
        position: absolute;
        background: #ddd;
        border-radius: 2px;
        width: 24px;
        height: 50px;
        top: 50%;
        left: 0;
      }

      .oc-audio-recorder-visual-2 {
        position: absolute;
        background: #ddd;
        border-radius: 2px;
        width: 24px;  
        height: 50px;
        top: 50%;
        left: 38px;
      }

      .oc-audio-recorder-visual-3 {
        position: absolute;
        background: #ddd;
        border-radius: 2px;
        width: 24px;
        height: 50px;
        top: 50%;
        left: 76px;
      }
    }
    .oc-audio-recorder {
      width: 50px;
      height: 50px;
      position: relative;

      .oc-audio-recorder-level {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 999px;
        background: rgba(0,0,0,0.1);
      }
    }
    .btn-circle {
      width: 50px;
      height: 50px;
      border-radius: 999px;
      line-height: 60px;
      padding: 0;
      position: relative;
    }
  `,
})
export class AudioRecorderComponent implements AfterViewInit {
  private ref = inject(ChangeDetectorRef);
  private toastr = inject(ToastrService);

  @Input() autoStart = false;
  @Input() visualise = false;
  @Output() audio: EventEmitter<Blob> = new EventEmitter();
  @Output() levels: EventEmitter<number> = new EventEmitter();

  recorder = new AudioRecorder();
  public recording = false;
  public recorded = false;

  public level = 0;

  playing = false;
  started = false;
  audioPlayer: InstanceType<typeof Audio> | null = null;

  blob: any = null;

  ngAfterViewInit(): void {
    if (this.autoStart) {
      this.record();
    }
  }

  async record() {
    try {
      if (this.recording) {
        this.blob = await this.recorder.stop();
        this.recording = false;
        this.recorded = true;
        this.level = 0;
        this.ref.detectChanges();
        this.audio.emit(this.blob);
      } else {
        await this.recorder.start();
        this.recorder.level$?.subscribe((level) => {
          const adjusted = 1 - Math.pow(level - 1, 6);
          this.level = adjusted;
          this.levels.next(level);
        });
        this.recording = true;
        this.ref.detectChanges();
      }
    } catch (err: any) {
      this.toastr.error(err['message']);
    }
  }

  async play() {
    if (!this.blob) {
      return;
    }

    if (this.started) {
      if (this.playing) {
        this.audioPlayer?.pause();
        this.playing = false;
        this.ref.detectChanges();
      } else {
        this.audioPlayer?.play();
        this.playing = true;
        this.ref.detectChanges();
      }
    } else {
      const blobURL = window.URL.createObjectURL(this.blob);
      this.audioPlayer = new Audio(blobURL);
      this.audioPlayer.addEventListener(
        'ended',
        () => {
          this.playing = false;
          this.started = false;
          this.ref.detectChanges();
        },
        false,
      );
      await this.audioPlayer.play();
      this.playing = true;
      this.started = true;
      this.ref.detectChanges();
    }
  }
}
